import {
  MutableRefObject, useCallback, useLayoutEffect, useState
} from "react";

import { isNotFunction } from "Utils/Function";
import { computeInnerDimensions } from "Utils/Node";
import { useDebouncedCallback } from "./UseDebouncedCallback";

export function useResizeObserver<T extends HTMLElement>(ref: MutableRefObject<T>): number {
  const [width, setWidth] = useState<number | null | undefined>(null);

  const updateWidth = useCallback(() => {
    if (ref.current == null) {
      return;
    }
    const { width: w } = computeInnerDimensions(ref.current);
    setWidth(w);
  }, [ref]);

  const updateWidthDebounced = useDebouncedCallback(updateWidth, 500);

  useLayoutEffect(() => {
    if (ref.current == null) {
      return () => undefined;
    }
    updateWidthDebounced();
    return addResizeObserver(ref.current, updateWidthDebounced);
  }, [ref, updateWidthDebounced]);

  return width;
}

function addResizeObserver(element: HTMLElement, callback: () => void): () => void {
  if (isNotFunction(ResizeObserver) || isNotFunction(callback)) {
    return () => undefined;
  }

  let observer = new ResizeObserver(callback);
  observer.observe(element);

  return () => {
    observer.disconnect();
    observer = null;
  };
}
